import { Combobox, Stack, useCombobox } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../../../store/global-store/global-store';
import {
  boxShadowBigSoft,
  fontFamily,
  fontSizeSmall,
  fontWeightBold,
  fontWeightMedium,
  inkDarkest,
  inkLight,
  skyBase,
  skyDark,
} from '../../../../../styles/design-tokens';
import { Button } from '../../../../../ui-library/button/button';
import { Divider } from '../../../../../ui-library/divider/divider';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Text } from '../../../../../ui-library/typography/typography';
import { useAvailableEntities } from './table.hooks';

type Props = {
  currentEntityId: string;
  currentEntityName: string;
  selectedEntities: string[];
  setSelectedEntities: (entityIds: string[]) => void;
};

export const EntityDropdown = ({
  currentEntityId,
  currentEntityName,
  selectedEntities,
  setSelectedEntities,
}: Props) => {
  const i18n = useTranslation();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const globalPortfolio = useGlobalStore((state) => state.portfolio);
  const availableEntities = useAvailableEntities();

  const filteredEntities = availableEntities.filter(
    (entity) => ![...selectedEntities, globalPortfolio?.id, currentEntityId].includes(entity.id),
  );

  const handleEntitySelect = (entityId: string) => {
    const updatedEntities = selectedEntities.map((id) => (id === currentEntityId ? entityId : id));
    setSelectedEntities(updatedEntities);
    combobox.closeDropdown();
  };

  const handleRemove = () => {
    const updatedEntities = selectedEntities.filter((id) => id !== currentEntityId);
    setSelectedEntities(updatedEntities);
    combobox.closeDropdown();
  };

  const options = filteredEntities.map((entity) => (
    <Combobox.Option
      value={entity.id}
      key={entity.id}
      p={0}
      style={{ cursor: 'pointer' }}
      onClick={() => handleEntitySelect(entity.id)}
    >
      <Text size="small" weight="medium">
        {entity.name}
      </Text>
    </Combobox.Option>
  ));

  return (
    <Combobox store={combobox} position="bottom-start" offset={{ mainAxis: 14, crossAxis: -18 }}>
      <Combobox.Target>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            gap: 10,
          }}
        >
          <span
            style={{
              fontSize: fontSizeSmall,
              fontWeight: fontWeightBold,
              lineHeight: '1em',
              color: inkDarkest,
              fontFamily,
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {currentEntityName}
          </span>
          <Combobox.Chevron onClick={() => combobox.toggleDropdown()} style={{ cursor: 'pointer' }} />
        </div>
      </Combobox.Target>

      <Combobox.Dropdown
        p={16}
        style={{
          borderRadius: 8,
          boxShadow: boxShadowBigSoft,
        }}
      >
        <Stack gap={16}>
          {options.length > 0 ? (
            <Combobox.Options
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                padding: 0,
              }}
            >
              {options}
            </Combobox.Options>
          ) : (
            <div
              style={{
                textAlign: 'center',
                color: skyDark,
                fontSize: fontSizeSmall,
                fontFamily,
              }}
            >
              {i18n.t('process_analysis.main_section.no_entities_available')}
            </div>
          )}
          <Divider style={{ margin: 0 }} />
          <Button
            variant="outline"
            radius="xl"
            size="sm"
            leftSection={<Icon name="close" color={inkLight} size={14} />}
            fullWidth
            onClick={handleRemove}
            style={{
              fontSize: fontSizeSmall,
              fontWeight: fontWeightMedium,
              color: inkLight,
              borderColor: skyBase,
              fontFamily,
            }}
          >
            {i18n.t('common.actions.remove')}
          </Button>
        </Stack>
      </Combobox.Dropdown>
    </Combobox>
  );
};
